import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { CreateEditTeamMeetingForm, ICreateEditTeamMeetingFormState } from ".";
import { v4 as newGuid } from "uuid";
import { useCreateTeamMeeting, useGetTeams } from "../../../../http";
import { IUser } from "../../../../models";
import { MeetingAgendaItemType } from "../../../../constants";

interface UrlParams {
  slug: string;
}

interface IProps {
  visible: boolean;
  onCreated: (guid: string) => void;
  onCancelled: () => void;
}

const CreateTeamMeetingModal = (props: IProps) => {
  const { slug } = useParams<keyof UrlParams>() as UrlParams;
  const { mutate: createMeeting, isLoading: isCreatingMeeting } = useCreateTeamMeeting();
  const { data: teams } = useGetTeams(undefined, props.visible);

  const { formState, control, handleSubmit, reset } = useForm<ICreateEditTeamMeetingFormState>({
    defaultValues: {
      title: "",
      description: "",
      externalCallUrl: "",
      invitees: [] as IUser[],
      agendaItems: [
        {
          guid: newGuid(),
          name: "Check In",
          description:
            "All team members give a brief check-in, acknowledging their full presence in the meeting, including any administrative concerns.",
          type: MeetingAgendaItemType.Blank,
          allocatedTimeInMinutes: 5,
          sortOrder: "0|hzzzzz:",
        },
        {
          guid: newGuid(),
          name: "Loops",
          description:
            "Members are given the opportunity to upvote any posted loops based on perceived importance. Loops are then processed in order of priority. If time runs out, the team begins asynchronous loop processing where possible.",
          type: MeetingAgendaItemType.TeamLoops,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|i00007:",
        },
        {
          guid: newGuid(),
          name: "Check Out",
          description: "Each team member offers a quick reflection of the meeting.",
          type: MeetingAgendaItemType.Blank,
          allocatedTimeInMinutes: 5,
          sortOrder: "0|i0000f:",
        },
      ],
    },
  });

  async function handleAddClicked() {
    const newMeetingGuid = newGuid();
    const team = teams?.find((x) => x.slug === slug);

    if (team == null) {
      return;
    }

    handleSubmit((data) => {
      createMeeting(
        {
          teamGuid: team.guid,
          meetingGuid: newMeetingGuid,
          title: data.title,
          description: data.description,
          externalCallUrl: data.externalCallUrl,
          invitees: data.invitees,
          agendaItems: data.agendaItems,
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });
            props.onCreated(newMeetingGuid);
          },
        },
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle sx={{ mb: 0 }}>Create Team Meeting</DialogTitle>
      <DialogContent sx={{ px: 0, pb: 0, pt: "10px !important" }}>
        <CreateEditTeamMeetingForm formState={formState} control={control} loadingEnabled={props.visible} />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ pt: 1 }}>
        <Button variant="outlined" disabled={isCreatingMeeting} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isCreatingMeeting}
          endIcon={isCreatingMeeting ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleAddClicked}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateTeamMeetingModal };
